import { ArrowUpRight } from '@phosphor-icons/react'
import {
  Children,
  Fragment,
  type ReactNode,
  useCallback,
  useRef,
  forwardRef,
} from 'react'
import { Box, type BoxProps, Row } from '../Box'
import { ButtonV3, type ButtonProps } from '../Button/Button.v3'
import { type LinkButtonProps } from '../Button/LinkButton.v3'
import { Divider } from '../Divider'
import { Icon } from '../Icon'
import { Link } from '../Link'
import { Popover, type PopoverProps, usePopover } from '../Popover'
import { Stack } from '../Stack'
import { TextWithCapsize as Text } from '../Text'
import {
  actionButtonItemCss,
  iconCss,
  listItemCss,
  listItemWithHoverCss,
} from './ActionButton.css'

// TODO: Uses Popover which uses sprinkles colors, so not yet fully v3

type BaseItem = {
  label: ReactNode
  onClick?: ButtonProps['onClick']
} & Omit<BoxProps, 'as' | 'onClick' | keyof ButtonProps>
type ButtonItem = Pick<ButtonProps, 'disabled'>
type LinkItem = { href: LinkButtonProps['href']; target?: '_blank' }

export type ActionItem = BaseItem & (ButtonItem | LinkItem)

export interface ActionButtonProps
  extends Omit<ButtonProps, 'title'>,
    Pick<PopoverProps, 'placement'> {
  actions: ActionItem[]
  title?: string | null
}

function wrapChildrenWithText(children: ReactNode) {
  return Children.map(children, (child, index) => {
    if (typeof child === 'string') {
      return (
        <Text as="span" variant={null} key={index}>
          {child}
        </Text>
      )
    }

    return child
  })
}

const isLinkItem = (item: ButtonItem | LinkItem): item is LinkItem =>
  !!(item as LinkItem)?.href

interface ActionMenuProps extends Pick<ActionButtonProps, 'actions'> {
  minWidth?: number
  closeMenu: () => void
}

function ActionItem({
  item,
  closeMenu,
}: {
  item: ActionItem
  closeMenu: () => void
}) {
  const { label, onClick, ...itemProps } = item

  return (
    <Row as="li" className={[listItemCss, listItemWithHoverCss]} role="option">
      {isLinkItem(itemProps) ? (
        // @ts-ignore
        <Row
          as={Link}
          {...itemProps}
          className={actionButtonItemCss}
          onClick={(e: any) => {
            // @ts-ignore
            onClick?.(e)
            closeMenu()
          }}
        >
          {wrapChildrenWithText(label)}
          <Icon as={ArrowUpRight} className={iconCss} />
        </Row>
      ) : (
        <Row
          as="button"
          className={actionButtonItemCss}
          /* @ts-ignore */
          variant={null}
          {...itemProps}
          onClick={(e: any) => {
            // @ts-ignore
            onClick?.(e)
            closeMenu()
          }}
        >
          {wrapChildrenWithText(label)}
        </Row>
      )}
    </Row>
  )
}

const ActionMenu = ({ minWidth, actions, closeMenu }: ActionMenuProps) => {
  return (
    <Stack as="ul" gap="1x" style={{ minWidth }}>
      {actions.map((item, i) => {
        return (
          <Fragment key={`item-${i}`}>
            <ActionItem item={item} closeMenu={closeMenu} />
            {i < actions.length - 1 ? (
              <Divider variant="secondary" bleedX="1x" />
            ) : null}
          </Fragment>
        )
      })}
    </Stack>
  )
}

const MenuTitle = ({ children }: { children: string }) => (
  <Box paddingX="1x">
    <Text>{children}</Text>
  </Box>
)

export const ActionButton = forwardRef<HTMLButtonElement, ActionButtonProps>(
  function ActionButton(
    { children, actions, placement = 'bottom', disabled, title, ...boxProps },
    forwardedRef,
  ) {
    const ref = useRef<HTMLButtonElement>(null)

    const { triggerProps, popoverProps, setOpen } = usePopover({
      placement,
      triggerRef:
        (typeof forwardedRef === 'function' ? ref : forwardedRef) || ref,
      trigger: 'menu',
    })

    const closeMenu = useCallback(() => {
      setOpen(false)
    }, [setOpen])

    return (
      <>
        <ButtonV3 {...boxProps} {...triggerProps} disabled={disabled}>
          {children}
        </ButtonV3>
        <Popover {...popoverProps}>
          <Stack gap="1x" margin="1x">
            {title && (
              <>
                <MenuTitle>{title}</MenuTitle>
                <Divider variant="secondary" bleedX="1x" />
              </>
            )}
            <ActionMenu actions={actions} closeMenu={closeMenu} />
          </Stack>
        </Popover>
      </>
    )
  },
)
